/**
 * Dictation, in place of the hub's action bar.
 *
 * This replaces a whole pushed screen (`medications/voice.tsx`) that carried an
 * engine picker, three blurbs and a scrolling transcript. None of that is a
 * decision a nurse should be making at a patient's kitchen table: she wants to
 * read the bottles out and see cards.
 *
 * It takes over the bottom bar rather than opening below the list, so the
 * control sits exactly where her thumb already is and the cards she is filling
 * stay on screen and in place behind it. While recording, the four actions the
 * bar normally offers are gone — there is nothing else to do mid-sentence.
 *
 * ENGINE, NOT OFFERED AS A CHOICE. The panel records (`audio_native`) on every
 * platform: one file, one model call when she stops, cards a few seconds
 * later. A streamed `live` engine used to run here on native builds; measured
 * on every production dictation it never showed a card before she stopped
 * anyway, and missed bottles the recording did not — see `describeEngine` and
 * docs/forms/ARCHITECTURE.md.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { useMedicationVoiceCapture } from "@/hooks/useMedicationVoiceCapture";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

interface Props {
    visitId: string;
    /** Drug names already on the visit — improves recognition of the rest. */
    knownMedicationNames?: string[];
    /** Close the panel. */
    onBack: () => void;
    /** A capture finished and wrote drafts — the hub refetches on this. */
    onCaptured: (attemptId: number) => void;
}

export function VoiceCapturePanel({
    visitId,
    knownMedicationNames = [],
    onBack,
    onCaptured
}: Props) {
    const capture = useMedicationVoiceCapture({
        visitId,
        knownMedicationNames,
        onSubmitted: onCaptured
    });
    const listening = capture.state === "listening";
    const busy = capture.state === "starting" || capture.state === "submitting";

    // Recording on every platform. Set once on mount: the nurse never picks,
    // and switching mid-session would lose whatever is already captured.
    const engineChosen = useRef(false);
    useEffect(() => {
        if (engineChosen.current) {
            return;
        }
        engineChosen.current = true;
        capture.setEngine("audio_native");
    }, [capture]);

    const status = busy
        ? capture.state === "submitting"
            ? "Reading your list…"
            : "Starting…"
        : listening
          ? "Listening — read the bottles out loud, cards appear when you stop"
          : "Tap to record";

    return (
        <View style={ss.panel}>
            <View style={ss.head}>
                <Pressable
                    onPress={onBack}
                    hitSlop={10}
                    style={ss.back}
                    accessibilityRole="button"
                    accessibilityLabel="Close voice capture"
                    disabled={listening || busy}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={22}
                        color={listening || busy ? colors.textTertiary : colors.teal600}
                    />
                    <Text style={[ss.backText, (listening || busy) && ss.backTextDisabled]}>
                        Back
                    </Text>
                </Pressable>
                {listening && <Text style={ss.timer}>{mmss(capture.seconds)}</Text>}
            </View>

            {capture.error && (
                <View style={ss.errorRow}>
                    <MaterialCommunityIcons
                        name="alert-circle-outline"
                        size={15}
                        color={colors.error500}
                    />
                    <Text style={ss.errorText}>{capture.error}</Text>
                </View>
            )}

            <Pressable
                onPress={listening ? capture.stop : capture.start}
                disabled={busy || !capture.available}
                style={[
                    ss.record,
                    listening && ss.recordListening,
                    (busy || !capture.available) && ss.recordDisabled
                ]}
                accessibilityRole="button"
                accessibilityState={{ busy, disabled: busy || !capture.available }}
                accessibilityLabel={listening ? "Stop recording" : "Start recording medications"}
            >
                {busy ? (
                    <ActivityIndicator color="#FFFFFF" />
                ) : (
                    <MaterialCommunityIcons
                        name={listening ? "stop" : "microphone"}
                        size={22}
                        color="#FFFFFF"
                    />
                )}
                <Text style={ss.recordText}>{listening ? "Stop recording" : "Record"}</Text>
            </Pressable>

            <Text style={ss.status}>{capture.unavailableReason ?? status}</Text>
        </View>
    );
}

function mmss(total: number): string {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

const ss = StyleSheet.create({
    // Deliberately the same chrome as StickyBottomBar — this IS that bar while
    // dictation is open, not a card floating over it.
    panel: {
        backgroundColor: colors.surface,
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        paddingBottom: 28,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 12,
        ...shadows.lg
    },
    head: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    back: { flexDirection: "row", alignItems: "center", marginLeft: -6 },
    backText: { fontSize: 14, fontFamily: fonts.semiBold, color: colors.teal600 },
    backTextDisabled: { color: colors.textTertiary },
    timer: {
        fontSize: 14,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        fontVariant: ["tabular-nums"]
    },
    errorRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    errorText: { flex: 1, fontSize: 12, fontFamily: fonts.medium, color: colors.error500 },
    record: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height: 52,
        borderRadius: radius.full,
        backgroundColor: colors.teal500
    },
    recordListening: { backgroundColor: colors.error500 },
    recordDisabled: { backgroundColor: colors.border },
    recordText: { fontSize: 15, fontFamily: fonts.extraBold, color: "#FFFFFF" },
    status: {
        textAlign: "center",
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    }
});
